import React, { createContext, useContext, useMemo } from 'react'

interface DataTableContextValue {
  actionBarFeedbackButtonLabel: string
  actionMenuButtonLabel: string
  actionMenuTitle: string
  configMenuOpenButtonLabel: string
  configMenuTitle: string
  configMenuPinnedColumnSubTitle: string
  configMenuRemainingColumnSubTitle: string
  configMenuResetButtonLabel: string
  configMenuSaveButtonLabel: string
  configMenuUndoButtonLabel: string
  countFetchingText: string
  getRowCountText: ({
    totalCount,
    selectedCount,
    isAllSelected,
  }: {
    totalCount: number
    selectedCount: number
    isAllSelected: boolean
  }) => string
  configMenuPinColumnButtonTooltip: string
  sortAscending: string
  sortDescending: string
  cleanSort: string
  selectAllBannerAllSelectedText: (count: number) => string
  selectAllBannerClearSelectionText: string
  selectAllBannerSelectAllText: (count: number) => string
  selectAllBannerVisibleSelectedText: (count: number) => string
}

const DataTableContext = createContext<DataTableContextValue | undefined>(undefined)

type DataTableProviderProps = DataTableContextValue & {
  children: React.ReactNode
}

export const DataTableProvider = ({
  actionBarFeedbackButtonLabel,
  actionMenuButtonLabel,
  actionMenuTitle,
  children,
  cleanSort,
  configMenuOpenButtonLabel,
  configMenuPinColumnButtonTooltip,
  configMenuPinnedColumnSubTitle,
  configMenuRemainingColumnSubTitle,
  configMenuResetButtonLabel,
  configMenuSaveButtonLabel,
  configMenuTitle,
  configMenuUndoButtonLabel,
  countFetchingText,
  getRowCountText,
  selectAllBannerAllSelectedText,
  selectAllBannerClearSelectionText,
  selectAllBannerSelectAllText,
  selectAllBannerVisibleSelectedText,
  sortAscending,
  sortDescending,
}: DataTableProviderProps) => {
  const dataTableConfig = useMemo(
    () => ({
      actionBarFeedbackButtonLabel,
      actionMenuButtonLabel,
      actionMenuTitle,
      cleanSort,
      configMenuOpenButtonLabel,
      configMenuPinColumnButtonTooltip,
      configMenuPinnedColumnSubTitle,
      configMenuRemainingColumnSubTitle,
      configMenuResetButtonLabel,
      configMenuSaveButtonLabel,
      configMenuTitle,
      configMenuUndoButtonLabel,
      countFetchingText,
      getRowCountText,
      selectAllBannerAllSelectedText,
      selectAllBannerClearSelectionText,
      selectAllBannerSelectAllText,
      selectAllBannerVisibleSelectedText,
      sortAscending,
      sortDescending,
    }),
    [
      actionBarFeedbackButtonLabel,
      actionMenuButtonLabel,
      actionMenuTitle,
      cleanSort,
      configMenuOpenButtonLabel,
      configMenuPinColumnButtonTooltip,
      configMenuPinnedColumnSubTitle,
      configMenuRemainingColumnSubTitle,
      configMenuResetButtonLabel,
      configMenuSaveButtonLabel,
      configMenuTitle,
      configMenuUndoButtonLabel,
      countFetchingText,
      getRowCountText,
      selectAllBannerAllSelectedText,
      selectAllBannerClearSelectionText,
      selectAllBannerSelectAllText,
      selectAllBannerVisibleSelectedText,
      sortAscending,
      sortDescending,
    ]
  )

  return <DataTableContext.Provider value={dataTableConfig}>{children}</DataTableContext.Provider>
}

export const useDataTableContext = (): DataTableContextValue => {
  const context = useContext(DataTableContext)

  if (context === undefined) {
    throw new Error('useDataTableContext must be used within a DataTableProvider')
  }

  return context
}
